import { Badge } from '@/components/ui/badge';
import {
  useConditionValueResolver,
  type ConditionValueResolver,
} from '@/hooks/use-condition-value-resolver';
import type { AssignmentRuleField, AssignmentRuleNode, ConditionOperator } from '@vagusx/shared';

const FIELD_LABELS: Record<AssignmentRuleField, string> = {
  priority: 'Priority',
  insurance: 'Insurance',
  provider: 'Provider',
  diagnoses: 'Diagnoses',
  services: 'Services',
  location: 'Location',
  label: 'Label',
};

const OPERATOR_LABELS: Record<ConditionOperator, string> = {
  equals: 'is',
  not_equals: 'is not',
  in: 'is one of',
  not_in: 'is not one of',
  contains_any: 'contains any of',
  contains_all: 'contains all of',
};

/**
 * Render a condition value for a Badge. Resolves slugs / UUIDs to
 * display names via the shared `useConditionValueResolver`. Truncates
 * long arrays to "first, second, +N" so a 9-label rule doesn't blow
 * up the row height.
 */
export function formatConditionValue(
  field: string,
  value: string | string[],
  resolve: ConditionValueResolver
): string {
  if (Array.isArray(value)) {
    const names = value.map((v) => resolve(field, v));
    if (names.length === 0) return '';
    if (names.length <= 2) return names.join(', ');
    return `${names[0]}, ${names[1]}, +${names.length - 2}`;
  }
  return resolve(field, value);
}

interface RuleSummaryProps {
  conditions: AssignmentRuleNode;
}

/**
 * Inline list-row summary for an assignment rule's condition tree.
 * Renders the top-level group's children with the parent's op
 * (`AND` / `OR`) between them, and pulls nested groups into
 * parenthesized sub-groups like `(A AND B) OR (C AND D)`.
 *
 * Past depth 2 the prose stops being a clean sentence — the rules
 * list is the wrong place for deep nesting anyway. PR B will replace
 * this with the LLM-generated summary cached on the row.
 */
export function RuleSummary({ conditions }: RuleSummaryProps): React.ReactElement {
  const resolve = useConditionValueResolver();

  if (conditions.kind === 'group' && conditions.children.length === 0) {
    return <span className="text-muted-foreground">No conditions</span>;
  }

  return (
    <span className="flex flex-wrap items-center gap-1">{renderNode(conditions, resolve)}</span>
  );
}

function renderNode(node: AssignmentRuleNode, resolve: ConditionValueResolver): React.ReactNode {
  if (node.kind === 'condition') {
    return (
      <Badge variant="outline" className="gap-1 py-0 text-[10px] font-normal">
        <span className="font-medium">{FIELD_LABELS[node.field] ?? node.field}</span>
        <span className="text-muted-foreground">
          {OPERATOR_LABELS[node.operator] ?? node.operator}
        </span>
        <span>{formatConditionValue(node.field, node.value, resolve)}</span>
      </Badge>
    );
  }

  // Group: render children joined by op connector. Wrap nested
  // (non-root) groups in parens so a reader can tell where one
  // sub-expression ends and the next begins.
  const connector = node.op === 'all' ? 'AND' : 'OR';
  return node.children.map((child, i) => (
    <span key={i} className="inline-flex flex-wrap items-center gap-1">
      {i > 0 && (
        <span className="text-[10px] font-semibold uppercase text-muted-foreground">
          {connector}
        </span>
      )}
      {child.kind === 'group' ? (
        <span className="inline-flex flex-wrap items-center gap-1">
          <span className="text-[10px] text-muted-foreground">(</span>
          {renderNode(child, resolve)}
          <span className="text-[10px] text-muted-foreground">)</span>
        </span>
      ) : (
        renderNode(child, resolve)
      )}
    </span>
  ));
}
